import type { ReactNode } from 'react';
import { strings } from '@/shared/strings';
import type { ColumnSort } from '@/domain';
import { ResponsiveTable, type Column, type TableSelection } from './ResponsiveTable';
import type { TableCustomize } from './CustomizeColumns';
import { Pagination, type PaginationProps } from './Pagination';
import { CaseMarks } from './CaseMarks';
import styles from './CaseTable.module.css';

/** What every case row is known by, whatever else the table is for. */
export interface CaseIdentity {
  jobOrderNo: string;
  /** Draws the red dot. Red means urgent and nothing else. */
  urgent?: boolean;
  /**
   * Draws the redo mark: the case is a remake of one that came back. Gold, never
   * red, and beside the urgent dot when a case is both. The request of
   * 6 October 2026 (second), item 2.
   */
  redo?: boolean;
  /** Draws the hold mark: the case is on hold. Slate, as a hold is everywhere. The request of 20 October 2026. */
  held?: boolean;
  /** Absent for a caller without seeIdentity, and rendered as Hidden. */
  dentistName?: string | null;
  patientName?: string | null;
}

export interface CaseTableProps<Row> {
  rows: Row[];
  /** Everything after Patient and before Status. */
  columns: Column<Row>[];
  identity: (row: Row) => CaseIdentity;
  status?: (row: Row) => ReactNode;
  /** What the Status column sorts by, since what it renders is not a scalar. */
  statusSortValue?: (row: Row) => string | number;
  rowKey: (row: Row) => string;
  onRowClick?: (row: Row) => void;
  caption: string;
  emptyHeading?: string;
  emptyDetail?: string;
  /** The order, held by the screen and shared with the bar's preset select. */
  sort?: ColumnSort | null;
  onSortChange?: (next: ColumnSort | null) => void;
  /**
   * The page under the rows, or nothing for a table that is never long.
   * Section 10.5 of the request of 16 September 2026 names which screens take
   * one: a technician's queue does not, because it is their own work, it is
   * never long, and a page control on it would be furniture in the one place
   * the system is trying to stay out of the way.
   */
  paging?: PaginationProps;
  /**
   * The boxes for acting on a selection of cases, when the screen offers any
   * action on one. Section 4 of the request of 24 September 2026. Each box is
   * named for its case, by the job order number the row leads with.
   */
  selection?: Omit<TableSelection<Row>, 'label'>;
  /**
   * One of the two name columns this table leaves out, because every row would
   * say the same thing in it: the Doctor on a doctor's own page, the Patient
   * on a patient's. The request of 6 October 2026 (fifth), section 4. Every
   * other screen lists the cases of many doctors and many patients, and leaves
   * this off.
   */
  without?: 'doctor' | 'patient';
  /**
   * The reader's own layout of this table, from `useTableLayout`: the columns
   * they chose, in their order. The request of 7 October 2026, section 3. The
   * job order number is essential and is always shown; a paged table offers
   * its rows a page in the same sheet.
   */
  customize?: Omit<TableCustomize, 'rows'>;
}

/** The labels a case table leads with. Exported so the tables test reads the same words. */
export const CASE_TABLE_LEADING_COLUMNS = [
  strings.table.jobOrder,
  strings.table.doctor,
  strings.table.patient,
] as const;

/**
 * The one case table.
 *
 * Job order, Doctor and Patient first, then whatever the screen is for, then
 * Status last, on every screen that lists cases, so the eye lands in the same
 * place on each. They were four tables built at four different times against
 * four different sections of the specification, and the kit id was the first
 * thing a manager read on the busiest screen in the lab. It is the small grey
 * line under the job order number now: still there where two trays of one order
 * sit at the same bench, and no longer the first thing anybody reads.
 *
 * A component rather than a convention, because a convention written in a
 * document drifts within a month and a component cannot.
 *
 * The identity cell is the job order number and nothing else. It carried the
 * kit id under it as a small grey line until the request of 13 September 2026,
 * section 8, which took it off the tables entirely: a job order is one tray,
 * and a number under every row that repeats the row above it is noise on the
 * screen a manager reads all day. It is still on the job view, the drawer's
 * kit cards, the printed sheet, the QR code and the scanner, which is
 * everywhere a person actually has to match a number to a tray.
 *
 * It renders Hidden where a name is absent and decides nothing about identity
 * itself. Whether a caller receives a name is the server's decision, and this
 * component never receives a name it is meant not to show.
 *
 * Below 760px it is `ResponsiveTable`'s cards, with the column label beside
 * every value, which is what makes the doctor and patient readable on a phone.
 */
export function CaseTable<Row>({
  rows,
  columns,
  identity,
  status,
  statusSortValue,
  rowKey,
  onRowClick,
  caption,
  emptyHeading,
  emptyDetail,
  sort,
  onSortChange,
  paging,
  selection,
  without,
  customize,
}: CaseTableProps<Row>) {
  const every: Column<Row>[] = [
    {
      key: 'jobOrderNo',
      label: strings.table.jobOrder,
      mono: true,
      sortable: true,
      sortValue: (row) => identity(row).jobOrderNo,
      render: (row) => {
        const { jobOrderNo, urgent, redo, held } = identity(row);
        return (
          <span className={styles.number}>
            <CaseMarks urgent={urgent === true} redo={redo === true} held={held === true} />
            {jobOrderNo}
          </span>
        );
      },
    },
    {
      key: 'dentistName',
      label: strings.table.doctor,
      sortable: true,
      sortValue: (row) => identity(row).dentistName ?? '',
      render: (row) => <Name value={identity(row).dentistName} />,
    },
    {
      key: 'patientName',
      label: strings.table.patient,
      sortable: true,
      sortValue: (row) => identity(row).patientName ?? '',
      render: (row) => <Name value={identity(row).patientName} />,
    },
  ];
  // The record the page is about is not repeated down every row of its own cases.
  const left = without === 'doctor' ? 'dentistName' : without === 'patient' ? 'patientName' : null;
  const leading = every.filter((column) => column.key !== left);

  const trailing: Column<Row>[] =
    status === undefined
      ? []
      : [
          {
            key: 'status',
            label: strings.table.status,
            sortable: statusSortValue !== undefined,
            sortValue: statusSortValue,
            render: status,
          },
        ];

  /*
   * What the screen only offers follows Status, so the columns the table comes
   * with keep their places, Status last among them, and a column the reader
   * ticks arrives after them until they move it. The request of 8 October 2026
   * (second).
   */
  const own = columns.filter((column) => column.optional !== true);
  const offered = columns.filter((column) => column.optional === true);

  return (
    <>
      <ResponsiveTable
        columns={[...leading, ...own, ...trailing, ...offered]}
        rows={rows}
        rowKey={rowKey}
        onRowClick={onRowClick}
        caption={caption}
        emptyHeading={emptyHeading}
        emptyDetail={emptyDetail}
        sortSelectOnMobile
        sort={sort}
        onSortChange={onSortChange}
        customize={
          customize === undefined
            ? undefined
            : {
                layout: customize.layout,
                save: customize.save,
                reset: customize.reset,
                essential: ['jobOrderNo'],
                rows:
                  paging === undefined
                    ? undefined
                    : { size: paging.pageSize, sizes: paging.sizes, choose: paging.onPageSize },
              }
        }
        selection={
          selection === undefined
            ? undefined
            : {
                ...selection,
                label: (row) => `${strings.bulk.selectCase} ${identity(row).jobOrderNo}`,
              }
        }
      />
      {paging !== undefined && <Pagination {...paging} />}
    </>
  );
}

/**
 * A name, or Hidden. The cell says the name was withheld rather than leaving a
 * gap the reader has to interpret.
 */
function Name({ value }: { value: string | null | undefined }) {
  if (value === null || value === undefined) {
    return <span className={styles.hidden}>{strings.table.hidden}</span>;
  }
  return <>{value}</>;
}
